import dayjs from 'dayjs'
import { MeetingCreateRequest } from '../../../../pages/NewProject/service/interfaces'
import { getActivities } from '../../../../pages/NewProject/service/projects'

// Template (Project Library) lessons carry a placeholder date the UI never shows.
// The backend still rejects two lessons with the same date and time in one project
// (SessionMeeting#same_date_and_time) and orders the list by that date, so every new
// lesson must land one day after the latest date already in use. Deriving it from the
// number of lessons breaks as soon as a lesson is deleted (the gap is never refilled)
// or the list is longer than one page.
export const TEMPLATE_LESSON_BASE_DATE = '2099-01-01'
export const TEMPLATE_LESSON_TIME = '08:00:00'
export const TEMPLATE_LESSON_DURATION = 60
// Retries only cover a race with another writer; the date comes from the full list.
export const TEMPLATE_LESSON_MAX_ATTEMPTS = 3

export const LESSONS_LOOKUP_FAILED = 'Could not load the template lessons'
// The shared api client resolves a 401 instead of throwing, so a body without the
// create contract (already_meetings) means nothing was created.
export const UNEXPECTED_CREATE_RESPONSE = 'Unexpected response while creating the template lesson'

const DATE_FORMAT = 'YYYY-MM-DD'

export interface LessonWithDate {
  readonly date?: string | null
}

export type TemplateLessonPayload = Omit<MeetingCreateRequest, 'date' | 'time' | 'duration' | 'dates_repeat_on'>

export interface CreateMeetingResponse {
  readonly alreadyMeetings?: string[]
}

export interface CreateTemplateLessonResult {
  readonly created: boolean
  readonly date: string
  readonly attempts: number
}

export interface CreateTemplateLessonOptions {
  // Lessons the caller has not loaded yet (the list is paginated); merged with `lessons`.
  readonly fetchLessonsInUse?: () => Promise<ReadonlyArray<LessonWithDate>>
  readonly maxAttempts?: number
}

interface LessonsPage {
  readonly sessionMeetings?: ReadonlyArray<LessonWithDate>
  readonly pagination?: { readonly pages?: number }
}

export const nextTemplateLessonDate = (lessons: ReadonlyArray<LessonWithDate>): string => {
  const base = dayjs(TEMPLATE_LESSON_BASE_DATE)

  const latest = lessons
    .map(lesson => (lesson.date ? dayjs(lesson.date) : null))
    .filter((date): date is dayjs.Dayjs => date !== null && date.isValid())
    .reduce<dayjs.Dayjs | null>((max, date) => (max === null || date.isAfter(max) ? date : max), null)

  if (latest === null || latest.isBefore(base)) return base.format(DATE_FORMAT)

  return latest.add(1, 'day').format(DATE_FORMAT)
}

// getActivities swallows a failed request and resolves undefined; an incomplete list
// would pick a date already in use, so a failed page aborts the creation instead.
const loadLessonsPage = async (projectId: string, page: number): Promise<LessonsPage> => {
  const response: LessonsPage | undefined = await getActivities(projectId, page)
  if (!response) throw new Error(LESSONS_LOOKUP_FAILED)

  return response
}

// The list endpoint is ordered by date and paginated, so the first and the last page
// together hold the latest date whatever the sort direction. At most two GETs.
export const fetchTemplateLessonsInUse = async (projectId: string): Promise<ReadonlyArray<LessonWithDate>> => {
  const firstPage = await loadLessonsPage(projectId, 1)
  const firstLessons = firstPage.sessionMeetings ?? []
  const pages = firstPage.pagination?.pages ?? 1

  if (pages <= 1) return firstLessons

  const lastPage = await loadLessonsPage(projectId, pages)

  return [...firstLessons, ...(lastPage.sessionMeetings ?? [])]
}

export const createTemplateLesson = async (
  create: (payload: MeetingCreateRequest) => Promise<CreateMeetingResponse | null | undefined>,
  payload: TemplateLessonPayload,
  lessons: ReadonlyArray<LessonWithDate>,
  options: CreateTemplateLessonOptions = {},
): Promise<CreateTemplateLessonResult> => {
  const maxAttempts = options.maxAttempts ?? TEMPLATE_LESSON_MAX_ATTEMPTS
  const lessonsInUse = options.fetchLessonsInUse ? [...lessons, ...(await options.fetchLessonsInUse())] : lessons

  let date = nextTemplateLessonDate(lessonsInUse)

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const response = await create({
      ...payload,
      date,
      time: TEMPLATE_LESSON_TIME,
      duration: TEMPLATE_LESSON_DURATION,
    })

    const alreadyMeetings = response?.alreadyMeetings
    if (!Array.isArray(alreadyMeetings)) throw new Error(UNEXPECTED_CREATE_RESPONSE)
    if (alreadyMeetings.length === 0) return { created: true, date, attempts: attempt }

    date = dayjs(date).add(1, 'day').format(DATE_FORMAT)
  }

  return { created: false, date, attempts: maxAttempts }
}
